/**
 * Embeddable read-only viewer for shared projects.
 * Renders framed artwork with frame, mat, effects, and scene.
 * Designed to be lightweight — no editor UI, no Zustand, no auth.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { cn } from '@frameit/ui';
import { getPublicViewer, ApiError } from '../../lib/api-client';
import { buildCssFilter } from '../editor/utils/color-filters';
import { computeEffectCSS } from '../editor/effects';
import { getFrameColor } from '../../data/frames';
import { getMatColor } from '../../data/mats';
import { renderFrame } from '../editor/renderers/frame-renderer';
import type { FramedArtwork, PublicViewerData } from '@frameit/types';

/** Render a single framed artwork */
function FramedArtworkView({
  artwork,
  imageUrl,
  maxSize = 400,
}: {
  artwork: FramedArtwork;
  imageUrl: string;
  maxSize?: number;
}): React.JSX.Element {
  const displayScale = artwork.displayScale ?? 1;
  const scale = displayScale;

  // Frame
  const frameColor = getFrameColor(artwork.frame.templateId);
  const frameWidth = (artwork.frame.width ?? 25) * 0.4 * scale;

  // Mat
  const matWidth = artwork.mat ? (artwork.mat.width ?? 0.1) * 200 * scale : 0;
  const totalBorder = frameWidth + matWidth;
  const matColor = artwork.mat ? getMatColor(artwork.mat.templateId) : undefined;
  const matHue = artwork.mat?.colorAdjustments?.hue ?? 0;
  const matSat = artwork.mat?.colorAdjustments?.saturation ?? 0;
  const matLight = artwork.mat?.colorAdjustments?.lightness ?? 0;
  const matFilter = `hue-rotate(${matHue}deg) saturate(${100 + matSat}%) brightness(${100 + matLight}%)`;

  // Color grading + effects
  const colorFilter = buildCssFilter(artwork.colorGrading);
  const effectStyles = artwork.effects
    .filter((e) => e.enabled)
    .map((e) => computeEffectCSS(e.templateId, e.params))
    .filter(Boolean);
  const effectFilterParts = effectStyles.map((s) => s?.filter).filter(Boolean);
  const combinedFilter =
    [colorFilter !== 'none' ? colorFilter : '', ...effectFilterParts].filter(Boolean).join(' ') ||
    undefined;

  // Shadow
  const shadow = artwork.shadow;
  const shadowEnabled = shadow?.enabled ?? false;
  const shadowCSS = shadowEnabled
    ? `${(shadow?.offsetX ?? 4) * scale}px ${(shadow?.offsetY ?? 4) * scale}px ${(shadow?.blur ?? 12) * scale}px ${shadow?.color ?? '#000000'}${Math.round(
        (shadow?.opacity ?? 0.3) * 255,
      )
        .toString(16)
        .padStart(2, '0')}`
    : 'none';

  // Frame canvas
  const innerW = Math.round(maxSize * scale + matWidth * 2);
  const innerH = Math.round(maxSize * scale + matWidth * 2);
  const frameImage = useMemo(() => {
    try {
      const canvas = renderFrame({
        templateId: artwork.frame.templateId,
        frameWidth: Math.round(frameWidth),
        innerWidth: innerW,
        innerHeight: innerH,
        highlightIntensity: artwork.frame.bevelAdjustments?.highlightIntensity,
        shadowIntensity: artwork.frame.bevelAdjustments?.shadowIntensity,
      });
      return canvas.toDataURL();
    } catch {
      return undefined;
    }
  }, [
    artwork.frame.templateId,
    frameWidth,
    innerW,
    innerH,
    artwork.frame.bevelAdjustments?.highlightIntensity,
    artwork.frame.bevelAdjustments?.shadowIntensity,
  ]);

  return (
    <div
      className="relative inline-block"
      style={{
        padding: `${totalBorder}px`,
        backgroundColor: frameColor,
        backgroundImage: frameImage ? `url(${frameImage})` : undefined,
        backgroundSize: '100% 100%',
        boxShadow: shadowCSS,
      }}
    >
      {/* Mat */}
      {artwork.mat && matColor && (
        <div
          className="absolute"
          style={{
            inset: `${frameWidth}px`,
            backgroundColor: matColor,
            filter: matFilter,
            boxShadow:
              'inset 2px 2px 4px rgba(255,255,255,0.15), inset -1px -1px 3px rgba(0,0,0,0.1)',
          }}
        />
      )}

      {/* Artwork image */}
      <img
        src={imageUrl}
        alt="Artwork"
        className="block"
        style={{
          maxWidth: maxSize * scale,
          maxHeight: maxSize * scale,
          objectFit: 'contain',
          filter: combinedFilter,
        }}
        crossOrigin="anonymous"
      />

      {/* Effect overlays */}
      {effectStyles.map(
        (es, i) =>
          es?.overlay && (
            <div
              key={i}
              className="absolute inset-0 pointer-events-none"
              style={{
                inset: `${totalBorder}px`,
                background: es.overlay,
                mixBlendMode:
                  (es.overlayBlendMode as React.CSSProperties['mixBlendMode']) ?? 'normal',
                opacity: es.overlayOpacity ?? 1,
              }}
            />
          ),
      )}
    </div>
  );
}

/** Embed viewer page — loaded at /embed/:token */
export function EmbedViewerPage(): React.JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicViewerData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [prevToken, setPrevToken] = useState(token);

  // Reset loading state synchronously when token changes (derived state during render)
  if (token !== prevToken) {
    setPrevToken(token);
    if (!isLoading) {
      setIsLoading(true);
    }
  }

  useEffect(() => {
    if (!token) return;
    getPublicViewer(token)
      .then((d) => {
        setData(d);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load');
        setIsLoading(false);
      });
  }, [token]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/20">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/20">
        <div className="text-center">
          <p className="text-muted-foreground mb-2">{error || 'Not found'}</p>
          <p className="text-xs text-muted-foreground">Powered by FrameIt</p>
        </div>
      </div>
    );
  }

  const artworks = data.framedArtworks;
  const currentArtwork = artworks[selectedIndex];
  const currentImageUrl = currentArtwork ? data.artworkUrls[currentArtwork.artworkId] : undefined;

  return (
    <div className="min-h-screen flex flex-col bg-muted/20">
      {/* Main artwork display */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-8">
        {currentArtwork && currentImageUrl ? (
          <FramedArtworkView
            artwork={currentArtwork}
            imageUrl={currentImageUrl}
            maxSize={Math.min(500, window.innerWidth - 64)}
          />
        ) : (
          <p className="text-muted-foreground">No artwork to display</p>
        )}
      </div>

      {/* Artwork selector (if multiple) */}
      {artworks.length > 1 && (
        <div className="flex justify-center gap-2 pb-4 px-4">
          {artworks.map((art, i) => (
            <button
              key={art.id}
              onClick={() => setSelectedIndex(i)}
              className={cn(
                'w-12 h-12 rounded-lg border-2 overflow-hidden transition-all',
                i === selectedIndex
                  ? 'border-primary ring-2 ring-primary/20'
                  : 'border-border hover:border-muted-foreground/50',
              )}
            >
              <img
                src={data.artworkUrls[art.artworkId]}
                alt={`Artwork ${i + 1}`}
                className="w-full h-full object-cover"
                crossOrigin="anonymous"
              />
            </button>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="text-center py-2">
        <span className="text-[10px] text-muted-foreground/60">
          {data.projectName} — Powered by FrameIt
        </span>
      </div>
    </div>
  );
}
